import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';
import { toPng } from 'html-to-image';
import { fmtPace, activityMeta } from '../utils/redemption';

// The hybrid-athlete "Rap Sheet": last night's Damage vs. this morning's
// Payback, stamped with a verdict. Two variants share one capture pipeline.
// Uses html-to-image (SVG foreignObject) rather than html2canvas: the browser
// rasterizes it, so text baselines/centering are pixel-faithful to the on-screen
// view (html2canvas renders text shifted down). Transparent background for overlay use.
//   variant="card"    — full post / in-app recap
//   variant="sticker" — lean bottom-band overlay for a story photo
// Pre-whitened logo (flat white silhouette, transparent bg) so no CSS filter is
// needed — html-to-image ghosts/doubles a `brightness-0 invert` filtered <img>.
const LOGO_PATH = '/bender-logo-white.png';

const VERDICT_STYLE = {
  paid_up:          { color: '#78d5c3', glow: 'rgba(120,213,195,0.55)' },
  in_debt:          { color: '#f4626e', glow: 'rgba(244,98,110,0.55)' },
  certified_hybrid: { color: '#e6d027', glow: 'rgba(230,208,39,0.55)' },
};

const fmtClock = (ms) => {
  if (!ms) return '—';
  const d = new Date(ms);
  let h = d.getHours(); const m = String(d.getMinutes()).padStart(2, '0');
  const ap = h >= 12 ? 'pm' : 'am'; h = h % 12 || 12;
  return `${h}:${m}${ap}`;
};
const fmtDur = (min) => {
  if (!min) return '—';
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
};

const RapSheetCard = forwardRef(({ sheet, variant = 'card' }, ref) => {
  const containerRef = useRef(null);
  const logoLoadedRef = useRef(false);

  useEffect(() => {
    const img = new Image();
    img.decoding = 'sync';
    img.crossOrigin = 'anonymous';
    img.onload = () => { logoLoadedRef.current = true; };
    img.onerror = () => { logoLoadedRef.current = true; };
    img.src = LOGO_PATH;
  }, []);

  useImperativeHandle(ref, () => ({
    generateImage: async () => {
      if (!containerRef.current) return null;
      let waited = 0;
      while (!logoLoadedRef.current && waited < 3000) { await new Promise(r => setTimeout(r, 100)); waited += 100; }
      await document.fonts.ready;
      await new Promise(r => setTimeout(r, 300));
      try {
        return await toPng(containerRef.current, {
          pixelRatio: 3,
          backgroundColor: undefined, // transparent — overlay-ready
        });
      } catch (e) { console.error('Rap sheet render failed:', e); return null; }
    },
  }));

  if (!sheet) return null;
  const { session, workout, redemption } = sheet;
  const v = VERDICT_STYLE[redemption.key] || VERDICT_STYLE.in_debt;
  const meta = activityMeta(workout?.type);
  const km = workout?.distanceKm ? workout.distanceKm.toFixed(2) : null;

  // ---------------- STICKER (lean overlay) ----------------
  if (variant === 'sticker') {
    return (
      <div ref={containerRef} className="w-[320px] p-3 flex flex-col gap-2 bg-transparent" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}>
        <span className="text-[9px] font-black text-white/70 uppercase tracking-[0.2em]">
          {fmtClock(session.endTime)} → {workout ? fmtClock(workout.startMs) : 'no workout'}
        </span>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-baseline gap-1.5 px-3 py-2 rounded-2xl bg-black/60 border border-white/20 text-white font-black italic text-2xl leading-none">
            <span className="not-italic text-base">🍺</span>{redemption.debt}
          </span>
          <span className="text-white/70 text-lg">→</span>
          <span className="inline-flex items-baseline gap-1.5 px-3 py-2 rounded-2xl bg-black/60 border border-white/20 text-white font-black italic text-2xl leading-none">
            <span className="not-italic text-base">{workout ? meta.emoji : '💤'}</span>
            {workout ? (km ? <>{km}<span className="text-xs text-white/70">km</span></> : fmtDur(workout.durationMin)) : '0'}
          </span>
        </div>
        <span className="self-start font-black italic uppercase text-lg px-3 py-1 border-2 -rotate-3 bg-black/50"
              style={{ color: v.color, borderColor: v.color, textShadow: `0 0 14px ${v.glow}` }}>
          {redemption.label}
        </span>
        <span className="text-[8px] font-black text-white/60 uppercase tracking-[0.25em]">Bender Quest · Rap Sheet</span>
      </div>
    );
  }

  // ---------------- FULL CARD ----------------
  return (
    <div ref={containerRef} className="w-[340px] bg-transparent border border-white/10 p-5 flex flex-col gap-4 text-white">
      <div className="flex justify-between items-start">
        <div className="font-black italic uppercase text-lg leading-none tracking-tighter">
          BENDER <span className="text-brand-pink">QUEST</span>
        </div>
        <div className="text-right text-[8px] font-mono uppercase tracking-[0.18em] text-zinc-500 leading-relaxed">
          <span className="block text-zinc-400 font-bold">Official Rap Sheet</span>
          {new Date(session.endTime).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
        </div>
      </div>

      <div className="text-center font-mono text-[10px] text-zinc-400 py-1.5 border-y border-zinc-800 tabular-nums">
        <span className="text-brand-pink">{fmtClock(session.endTime)}</span>
        <span className="text-zinc-600 mx-2">→</span>
        <span className="text-brand-teal">{workout ? fmtClock(workout.startMs) : 'no workout'}</span>
      </div>

      {/* THE DAMAGE */}
      <div className="relative pl-3">
        <div className="absolute left-0 top-0.5 bottom-0.5 w-1 bg-brand-pink" style={{ boxShadow: '0 0 10px rgba(230,131,169,0.6)' }} />
        <div className="flex justify-between items-baseline mb-2">
          <span className="font-black italic uppercase text-sm text-brand-pink">The Damage</span>
          <span className="font-mono text-[8px] uppercase tracking-[0.16em] text-zinc-500">Last night</span>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
          <Stat n={session.drinks || 0} l="Drinks" />
          <Stat n={fmtClock(session.endTime)} l="Last call" />
          <Stat n={(session.steps || 0).toLocaleString()} l="Night steps" />
          <Stat n={`${Math.round(session.hydration ?? 0)}%`} l="Hydration left" />
        </div>
        {session.drinkOfChoice && session.drinkOfChoice !== 'PICK POISON' && session.drinkOfChoice !== 'CHOOSE DRINK' && (
          <span className="mt-2 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-zinc-400 px-2 py-1 border border-zinc-800 bg-zinc-900">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-pink" />Drink · {session.drinkOfChoice}
          </span>
        )}
      </div>

      {/* CENTERPIECE — semi-transparent so an overlay photo shows through. When a
          route map lands here (build 6), it fills the box and can go opaque. */}
      <div className="border border-white/15 bg-black/40 py-4 flex flex-col items-center gap-1 text-center">
        <span className="text-2xl leading-none">{workout ? meta.emoji : '💤'}</span>
        {workout ? (
          <span className="font-black italic text-4xl leading-none tabular-nums">
            {km ? <>{km}<span className="text-sm text-zinc-400 ml-1">km</span></> : fmtDur(workout.durationMin)}
          </span>
        ) : (
          <span className="font-black italic text-3xl leading-none text-zinc-500">0.0<span className="text-sm ml-1">km</span></span>
        )}
        <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-brand-teal">
          {workout ? (km ? 'Distance' : 'Time under fire') : 'No workout found'}
        </span>
      </div>

      {/* THE PAYBACK */}
      <div className="relative pl-3">
        <div className="absolute left-0 top-0.5 bottom-0.5 w-1 bg-brand-teal" style={{ boxShadow: '0 0 10px rgba(120,213,195,0.6)' }} />
        <div className="flex justify-between items-baseline mb-2">
          <span className="font-black italic uppercase text-sm text-brand-teal">The Payback</span>
          <span className="font-mono text-[8px] uppercase tracking-[0.16em] text-zinc-500">
            This morning{workout ? ` · ${meta.label}` : ''}
          </span>
        </div>
        {workout ? (
          // Only workout type/duration + distance are read (Play Health Connect
          // "minimum scope" — calories + heart rate were rejected as non-essential
          // garnish, and they were). Tiles adapt so nothing renders as a dash.
          (() => {
            const tiles = [];
            if (workout.paceSecPerKm) tiles.push([fmtPace(workout.paceSecPerKm), 'Pace /km']);
            if ((workout.distanceKm || 0) > 0.2) tiles.push([workout.distanceKm.toFixed(1), 'km']);
            tiles.push([fmtDur(workout.durationMin), 'Moving']);
            return (
              <div className={`grid gap-x-2 gap-y-2.5 ${tiles.length === 3 ? 'grid-cols-3' : tiles.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {tiles.map(([n, l]) => <Stat key={l} n={n} l={l} />)}
              </div>
            );
          })()
        ) : (
          <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Nothing logged in the redemption window.</p>
        )}
        {workout?.source && (
          <span className="mt-2 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-zinc-400 px-2 py-1 border border-zinc-800 bg-zinc-900">
            Imported · {workout.source}
          </span>
        )}
      </div>

      {/* HANGOVER HANDICAP */}
      {redemption.handicap && (
        <div className="border border-dashed border-zinc-700 bg-zinc-900 px-3 py-2 flex items-center justify-between gap-3">
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-400 leading-relaxed">
            Hangover Handicap<br />
            <span className="text-white font-bold">+{fmtPace(redemption.handicap.penaltySec)}/km</span> for {redemption.debt} drinks
          </div>
          <div className="text-right">
            <div className="font-black italic text-xl tabular-nums" style={{ color: '#e6d027' }}>{fmtPace(redemption.handicap.adjPaceSec)}</div>
            <div className="font-mono text-[8px] uppercase tracking-wider text-zinc-500">adjusted pace</div>
          </div>
        </div>
      )}

      {/* VERDICT */}
      <div className="border-2 px-3 py-3 flex items-center justify-center gap-3"
           style={{ borderColor: v.color, color: v.color, backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.015) 0 10px, transparent 10px 20px)' }}>
        <span className="font-black italic uppercase text-2xl leading-none -rotate-3" style={{ textShadow: `0 0 18px ${v.glow}` }}>
          {redemption.label}
        </span>
        <span className="font-mono text-[8px] uppercase tracking-[0.14em] text-zinc-400 max-w-[12ch] leading-snug">{redemption.sub}</span>
      </div>

      <div className="flex items-center justify-between font-mono text-[8px] uppercase tracking-wider text-zinc-600">
        <span>{redemption.paid} / {redemption.debt} paid</span>
        <img src={LOGO_PATH} alt="Bender Extender" decoding="sync" crossOrigin="anonymous" className="opacity-70 block" style={{ width: 56, height: 38 }} />
      </div>
    </div>
  );
});

const Stat = ({ n, l }) => (
  <div className="flex flex-col gap-0.5">
    <span className="font-black italic text-2xl leading-none tabular-nums">{n}</span>
    <span className="font-mono text-[8px] uppercase tracking-[0.14em] text-zinc-500">{l}</span>
  </div>
);

RapSheetCard.displayName = 'RapSheetCard';
export default RapSheetCard;
